import Phaser from 'phaser';
import { PLAYER } from '../config';
import type { ControlState } from '../input/Controls';
import { Vehicle } from './Vehicle';

export type PlayerMode = 'OnFoot' | 'InVehicle';

export class Player {
  readonly scene: Phaser.Scene;
  readonly sprite: Phaser.Physics.Arcade.Sprite;
  hp = PLAYER.health;
  cash = 0;
  mode: PlayerMode = 'OnFoot';
  vehicle: Vehicle | null = null;
  facing = 0; // radians, 0 = up
  private lastPunchAt = 0;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.scene = scene;
    const s = scene.physics.add.sprite(x, y, 'player');
    s.setCollideWorldBounds(true);
    s.setScale(0.55);
    // Tight body so collisions feel right (sprites have weapon overhang)
    s.body!.setSize(18, 18);
    s.body!.setOffset((s.width - 18) / 2, (s.height - 18) / 2);
    s.setData('player', this);
    this.sprite = s;
  }

  enterVehicle(v: Vehicle) {
    if (this.mode === 'InVehicle') return;
    this.vehicle = v;
    v.driver = 'player';
    this.mode = 'InVehicle';
    this.sprite.setVisible(false);
    (this.sprite.body as Phaser.Physics.Arcade.Body).enable = false;
  }

  exitVehicle() {
    if (this.mode !== 'InVehicle' || !this.vehicle) return;
    const v = this.vehicle;
    const offX = Math.cos(v.sprite.rotation - Math.PI / 2 + Math.PI / 2) * (v.stats.width + 8);
    const offY = Math.sin(v.sprite.rotation - Math.PI / 2 + Math.PI / 2) * (v.stats.width + 8);
    this.sprite.setPosition(v.sprite.x + offX, v.sprite.y + offY);
    this.sprite.setVisible(true);
    (this.sprite.body as Phaser.Physics.Arcade.Body).enable = true;
    v.driver = 'none';
    v.sprite.setAcceleration(0, 0);
    this.vehicle = null;
    this.mode = 'OnFoot';
  }

  /** World position regardless of mode */
  pos(): { x: number; y: number } {
    if (this.mode === 'InVehicle' && this.vehicle) {
      return { x: this.vehicle.sprite.x, y: this.vehicle.sprite.y };
    }
    return { x: this.sprite.x, y: this.sprite.y };
  }

  damage(n: number) { this.hp = Math.max(0, this.hp - n); }
  heal(n: number)   { this.hp = Math.min(PLAYER.health, this.hp + n); }

  update(c: ControlState, dt: number) {
    if (this.mode === 'OnFoot') {
      const sp = PLAYER.walkSpeed;
      let vx = 0, vy = 0;
      if (c.left)  vx -= 1;
      if (c.right) vx += 1;
      if (c.up)    vy -= 1;
      if (c.down)  vy += 1;
      if (vx || vy) {
        const len = Math.hypot(vx, vy);
        vx /= len; vy /= len;
        this.facing = Math.atan2(vx, -vy);
        this.sprite.rotation = this.facing;
      }
      this.sprite.setVelocity(vx * sp, vy * sp);
    } else if (this.mode === 'InVehicle' && this.vehicle) {
      let throttle = 0;
      if (c.up)   throttle += 1;
      if (c.down) throttle -= 0.7;
      let steer = 0;
      if (c.left)  steer -= 1;
      if (c.right) steer += 1;
      this.vehicle.drive(throttle, steer, dt);
    }
  }

  canPunch(now: number) { return now - this.lastPunchAt >= PLAYER.punchCooldownMs; }
  registerPunch(now: number) { this.lastPunchAt = now; }
}
